import { Button, Card, Col, Form, Input, InputNumber, Row, Table, message } from "antd";

import {
  useCreateProductCategoryMutation,
  useDeleteProductCategoryMutation,
  useGetAllMyProductCategoriesQuery,
  useUpdateProductCategoryMutation,
} from "../graphql/generated/hooks";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import SimpleEditModal from "../components/SimpleEditModal";
import RobinPage from "../components/core/RobinPage";
import EditDeleteActions from "../components/EditDeleteActions";

function CategoryList() {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [createForm] = Form.useForm();
  const [canCreate, setCanCreate] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState<number>();

  const {
    data: categoriesData,
    loading,
    refetch,
  } = useGetAllMyProductCategoriesQuery();
  const [addCategory, { loading: creating }] =
    useCreateProductCategoryMutation();
  const [deleteCategory, { loading: deleting }] =
    useDeleteProductCategoryMutation();
  const [updateCategory, { loading: updating }] =
    useUpdateProductCategoryMutation();

  const dataSource = useMemo(
    () =>
      categoriesData?.getAllProductCategoriesForPartner?.map((category, index) => ({
        key: index,
        name: category.name,
        nameAr: category.nameAr,
        order: category.order,
        actions: (
          <EditDeleteActions
            onEdit={() => {
              setEditCategoryId(category.id);
              setEditMode(true);
            }}
            onDelete={() => {
              deleteCategory({ variables: { id: category.id } })
                .then(() => {
                  messageApi.success(t("message.success.data_deleted"));
                  refetch();
                })
                .catch(() => {
                  messageApi.error(t("message.error.data_delete_failed"));
                });
            }}
            loadingDelete={deleting}
          />
        ),
      })) ?? [],
    [categoriesData?.getAllProductCategoriesForPartner, deleteCategory, deleting, messageApi, refetch, t]
  );

  return (
    <RobinPage title={t("label.menu.product_categories")}>
      <Row gutter={[10, 10]}>
        <SimpleEditModal
          loading={updating}
          isOpen={editMode}
          title={t("title.edit_category")}
          items={[
            { label: t("label.name"), name: "name", type: "text" },
            { label: t("label.name_ar"), name: "nameAr", type: "text" },
            { label: t("label.category_position"), name: "order", type: "number" },
          ]}
          initialValues={categoriesData?.getAllProductCategoriesForPartner.find(
            (rec) => rec.id === editCategoryId
          )}
          handleOk={(values: {
            name: string;
            nameAr: string;
            order: number;
          }) => {
            if (editCategoryId && values)
              updateCategory({
                variables: {
                  id: editCategoryId,
                  name: values.name ?? undefined,
                  nameAr: values.nameAr ?? undefined,
                  order: +values.order,
                },
              }).then(() => {
                setEditCategoryId(undefined);
                setEditMode(false);
                refetch();
              });
          }}
          handleCancel={() => {
            setEditMode(false);
            setEditCategoryId(undefined);
          }}
        />
        {contextHolder}
        <Col span={24}>
          <Card title={t("title.add_category")} style={{ borderRadius: 12 }}>
            <Form
              form={createForm}
              layout="vertical"
              initialValues={{ name: "", nameAr: "", order: 0 }}
              onValuesChange={() => {
                createForm
                  .validateFields({ validateOnly: true })
                  .then(() => setCanCreate(true))
                  .catch(() => setCanCreate(false));
              }}
              onFinish={async (values: { name: string; nameAr: string; order: number }) => {
                await addCategory({
                  variables: {
                    name: values.name,
                    nameAr: values.nameAr,
                    order: Number(values.order ?? 0),
                  },
                })
                  .then(() => {
                    messageApi.success(t("message.success.data_created"));
                    refetch();
                    createForm.resetFields();
                    createForm.setFieldsValue({ order: 0 });
                    setCanCreate(false);
                  })
                  .catch(() => {
                    messageApi.error(t("message.error.data_create_failed"));
                  });
              }}
            >
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item
                    label={t("label.name")}
                    name="name"
                    rules={[{ required: true, message: t("message.error.field_required") }]}
                  >
                    <Input placeholder={t("label.name")} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label={t("label.name_ar")}
                    name="nameAr"
                    rules={[{ required: true, message: t("message.error.field_required") }]}
                  >
                    <Input placeholder={t("label.name_ar")} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    label={t("label.category_position")}
                    tooltip={t("tooltip.category_position")}
                    name="order"
                  >
                    <InputNumber style={{ width: "100%" }} min={0} precision={0} />
                  </Form.Item>
                </Col>
              </Row>

              <Row justify="end">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={creating}
                  disabled={!canCreate || creating}
                >
                  {t("button.add")}
                </Button>
              </Row>
            </Form>
          </Card>
        </Col>
        <Col span={24}>
          <Table
            loading={loading}
            dataSource={dataSource}
            columns={[
              {
                title: t("label.name"),
                dataIndex: "name",
                key: "name",
              },
              {
                title: t("label.name_ar"),
                dataIndex: "nameAr",
                key: "nameAr",
              },
              {
                title: t("label.category_position"),
                dataIndex: "order",
                key: "order",
              },
              { title: t("label.actions"), dataIndex: "actions", key: "actions" },
            ]}
          />
        </Col>
      </Row>
    </RobinPage>
  );
}

export default CategoryList;
